import { listTrips, setArchived, type TripSummary } from '@trippath/shared';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

/** What one may do with the trip open: whether it is read only, archived or seen only, and the
 * way to take it out of the archive. */
interface Access {
  /** Nothing of it changed: archived, or only seen. */
  locked: boolean;
  /** Put away by its owner, read only till taken out. */
  archived: boolean;
  /** Its owner, who takes it out of the archive. */
  owner: boolean;
  /** Taken out of the archive: changed again as before. */
  restore: () => Promise<void>;
}

const OPEN: Access = {
  locked: false,
  archived: false,
  owner: false,
  restore: () => Promise.resolve(),
};

const AccessContext = createContext<Access>(OPEN);

/** Whether the trip open is read only: every page leaves out what would change it. */
export function useReadOnly(): boolean {
  return useContext(AccessContext).locked;
}

/** What one may do with the trip open, and how it is taken out of the archive. */
export function useTripAccess(): Access {
  return useContext(AccessContext);
}

/**
 * The trip open as the trips list says it: archived or only seen, it is read only on each of its
 * pages. Until the list is had, nothing is held back — the server refuses a change anyway.
 */
export function TripAccess({ trip, children }: { trip: string | null; children: ReactNode }) {
  const [summary, setSummary] = useState<TripSummary | null>(null);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    if (trip === null) return;
    let live = true;
    listTrips().then(
      (trips) => {
        if (live) setSummary(trips.find((one) => one.id === trip) ?? null);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [trip, asked]);
  const shown = summary?.id === trip ? summary : null;
  const archived = shown !== null && shown.archivedAt !== null;
  const access: Access = {
    locked: archived || shown?.role === 'viewer',
    archived,
    owner: shown?.role === 'owner',
    restore: () =>
      trip === null
        ? Promise.resolve()
        : setArchived(trip, false).then(() => {
            setAsked((n) => n + 1);
          }),
  };
  return <AccessContext.Provider value={access}>{children}</AccessContext.Provider>;
}

/** What changes the trip, held still on one read only: every field and button in it disabled,
 * its links still followed. It takes no room of its own. */
export function ReadOnlySet({ children }: { children: ReactNode }) {
  return (
    <fieldset className="read-only-set" disabled={useReadOnly()}>
      {children}
    </fieldset>
  );
}

/** A page that is there only to change the trip — a mail read in, a way drawn, a way recorded,
 * places added from the map: on one read only, that it is, and the way back to its plan. */
export function ChangesOnly({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  if (!useReadOnly()) return children;
  return (
    <div className="page">
      <p>{t('trips.readOnlyNote')}</p>
      <Link to={`/trips/${id}`}>{t('sights.back')}</Link>
    </div>
  );
}

/** Over an archived trip's pages, or `inline` in its settings: that it is read only, and for
 * its owner the way out. */
export function ArchivedBanner({ inline = false }: { inline?: boolean }) {
  const { t } = useTranslation();
  const { archived, owner, restore } = useTripAccess();
  const [failed, setFailed] = useState(false);
  if (!archived) return null;
  return (
    <div
      className={inline ? 'archived-banner archived-inline' : 'archived-banner'}
      role="status"
    >
      <p>{t('trips.archivedNote')}</p>
      {owner && (
        <button
          type="button"
          className="button button-small"
          onClick={() => {
            setFailed(false);
            restore().catch(() => {
              setFailed(true);
            });
          }}
        >
          {t('trips.restore')}
        </button>
      )}
      {failed && (
        <p className="form-failed" role="alert">
          {t('trips.actionFailed')}
        </p>
      )}
    </div>
  );
}
